import React, { useState } from 'react';
import { setViewMode } from '../../shared/utils/desktopViewport';
import {
    Home, ShoppingBag, Users, Star, LayoutGrid, X, LogOut,
    FileText, Tag, Truck, Radar, PieChart, Headphones, LifeBuoy, ShieldAlert,
    CheckSquare, Calendar, UserCog, MessageSquare, Settings, ShoppingCart, Gift, Archive, Ban, Package,
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAuth, signOut } from 'firebase/auth';
import { cn } from '../../shared/utils';
import { useChat } from '../../features/chat/state/ChatContext';
import { can, canUser } from '../../core/permissions';
import { moduleAllowed, MODULE_REGISTRY } from '../../core/permissions/modules';

/**
 * The phone's navigation, rebuilt around how the owner actually runs the shop.
 *
 * The old bar had five fixed tabs (Home, Customers, Orders, Chat, Settings) and no way to
 * reach anything else — and the mobile router sent every other path back to the
 * dashboard, so Rewards, the Quick Order funnel, the courier desks and complaints simply
 * did not exist on a phone. The four tabs here are the four screens a shop owner opens
 * on the move, and MORE is the whole rest of the app: every module their role allows,
 * the same registry and permission checks the desktop sidebar uses, so the two can never
 * disagree about what someone may see.
 */

// What each module looks like in the More sheet. Anything in MODULE_REGISTRY but not
// here still renders — with a sensible default icon — so a future module is never
// invisible on mobile the way new modules were once invisible in the sidebar.
const MODULE_ICONS = {
    dashboard: Home, orders: ShoppingBag, invoices: FileText, labels: Tag,
    tracking: Radar, insights: PieChart, products: Package, parser: FileText,
    customers: Users, telecaller: Headphones, complaints: LifeBuoy,
    'quick-order': ShoppingCart, escalations: ShieldAlert, todo: CheckSquare,
    calendar: Calendar, staff: UserCog, chat: MessageSquare, analytics: PieChart,
    referrals: Gift, rewards: Star, settings: Settings,
};

// The sheet also carries the places that are not "modules" but are part of running
// orders day to day.
const EXTRA_LINKS = [
    { key: 'shiprocket', label: 'Shiprocket', icon: Truck, capability: 'canViewOrders' },
    { key: 'dtdc', label: 'DTDC', icon: Truck, capability: 'canViewOrders' },
    { key: 'india-post', label: 'India Post', icon: Truck, capability: 'canViewOrders' },
    { key: 'ekart', label: 'Ekart', icon: Truck, capability: 'canViewOrders' },
    { key: 'archived', label: 'Archived Orders', icon: Archive, capability: 'canViewOrders' },
    { key: 'cancelled', label: 'Cancelled Orders', icon: Ban, capability: 'canViewOrders' },
    // Full-screen route (not a dashboard tab) — the warehouse scan desk.
    { key: 'handover', label: 'Handover Scan', icon: Truck, capability: 'canViewHandover', route: '/handover' },
    { key: 'qc', label: 'Parcel QC', icon: Truck, capability: 'canViewQc', route: '/qc' },
];

const NavItem = ({ icon: Icon, label, isActive, onClick }) => (
    <button
        onClick={onClick}
        className={cn(
            'flex flex-col items-center justify-center w-full py-2 transition-all duration-300',
            isActive ? 'text-purple-600' : 'text-slate-400 hover:text-slate-600',
        )}
    >
        <div className={cn(
            'p-1.5 rounded-xl transition-all duration-300 mb-1',
            isActive ? 'bg-purple-50 transform -translate-y-1 shadow-sm' : 'bg-transparent',
        )}>
            <Icon size={20} strokeWidth={isActive ? 2.5 : 2} />
        </div>
        <span className={cn('text-[10px] font-medium', isActive ? 'opacity-100 font-bold' : 'opacity-80')}>
            {label}
        </span>
    </button>
);

const MobileNavbar = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [moreOpen, setMoreOpen] = useState(false);
    const { currentUser } = useChat();

    const amCtx = { savedAt: currentUser?.allowedModulesSavedAt, role: currentUser?.role };
    const show = (cap, key) => canUser(currentUser, cap) && moduleAllowed(currentUser?.allowedModules, key, amCtx);

    const TABS = [
        { key: 'home', label: 'Home', icon: Home, path: '/dashboard', match: (p) => p === '/' || p === '/dashboard' },
        { key: 'orders', label: 'Orders', icon: ShoppingBag, path: '/orders', match: (p) => p.startsWith('/orders') },
        { key: 'customers', label: 'Customers', icon: Users, path: '/customers', match: (p) => p.startsWith('/customers') },
        { key: 'rewards', label: 'Rewards', icon: Star, path: '/rewards', match: (p) => p.startsWith('/rewards') },
    ];
    const tabPaths = ['/', '/dashboard', '/orders', '/customers', '/rewards'];
    const onATab = tabPaths.some(p => location.pathname === p) || TABS.some(t => t.match(location.pathname));

    const moreModules = MODULE_REGISTRY
        .filter(m => !['dashboard', 'orders', 'customers', 'rewards'].includes(m.key))
        .filter(m => show(m.capability, m.key));
    const extras = EXTRA_LINKS.filter(l => can(currentUser?.role, l.capability));

    const go = (path) => { setMoreOpen(false); navigate(path); };

    const logout = async () => {
        if (!window.confirm('Log out of Team Daksha?')) return;
        try { await signOut(getAuth()); } catch { /* the auth gate routes to /login regardless */ }
        navigate('/login');
    };

    return (
        <>
            {/* The sheet: the whole rest of the app, one tap away. */}
            {moreOpen && (
                <div className="fixed inset-0 z-[60]" role="dialog" aria-label="All modules">
                    <div className="absolute inset-0 bg-black/40" onClick={() => setMoreOpen(false)} />
                    <div className="absolute bottom-0 inset-x-0 bg-white rounded-t-3xl max-h-[80vh] overflow-y-auto pb-safe shadow-2xl">
                        <div className="sticky top-0 bg-white px-5 pt-4 pb-2 flex items-center rounded-t-3xl">
                            <div>
                                <div className="font-black text-slate-800">Everything else</div>
                                <div className="text-[11px] text-slate-400">
                                    {currentUser?.name ? `Signed in as ${currentUser.name}` : 'All your modules'}
                                </div>
                            </div>
                            <button onClick={() => setMoreOpen(false)} className="ml-auto p-2 rounded-full bg-slate-100 text-slate-500" aria-label="Close">
                                <X size={18} />
                            </button>
                        </div>

                        <div className="px-3 pt-1">
                            <button type="button" onClick={() => setViewMode('desktop')}
                                className="w-full flex items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm font-bold text-slate-700 active:bg-slate-100">
                                <LayoutGrid size={18} className="text-purple-600" /> Switch to desktop view
                                <span className="ml-auto text-[11px] font-semibold text-slate-400">full screens, zoomed out</span>
                            </button>
                        </div>
                        <div className="grid grid-cols-4 gap-1 px-3 pt-2">
                            {moreModules.map(m => {
                                const Icon = MODULE_ICONS[m.key] || LayoutGrid;
                                const active = location.pathname === `/${m.key}`;
                                return (
                                    <button key={m.key} onClick={() => go(`/${m.key}`)}
                                        className={cn('flex flex-col items-center gap-1.5 rounded-2xl py-3 px-1', active ? 'bg-purple-50' : 'active:bg-slate-50')}>
                                        <span className={cn('w-11 h-11 rounded-2xl flex items-center justify-center', active ? 'bg-purple-600 text-white' : 'bg-slate-100 text-slate-600')}>
                                            <Icon size={20} />
                                        </span>
                                        <span className="text-[10.5px] font-semibold text-slate-600 text-center leading-tight">{m.label}</span>
                                    </button>
                                );
                            })}
                        </div>

                        {extras.length > 0 && (
                            <>
                                <div className="px-5 pt-4 pb-1 text-[10px] font-bold uppercase tracking-wider text-slate-400">Ship &amp; couriers</div>
                                <div className="grid grid-cols-4 gap-1 px-3">
                                    {extras.map(l => (
                                        <button key={l.key} onClick={() => go(`/${l.key}`)}
                                            className="flex flex-col items-center gap-1.5 rounded-2xl py-3 px-1 active:bg-slate-50">
                                            <span className="w-11 h-11 rounded-2xl bg-slate-100 text-slate-600 flex items-center justify-center"><l.icon size={20} /></span>
                                            <span className="text-[10.5px] font-semibold text-slate-600 text-center leading-tight">{l.label}</span>
                                        </button>
                                    ))}
                                </div>
                            </>
                        )}

                        <div className="px-5 py-4">
                            <button onClick={logout} className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl bg-rose-50 text-rose-600 text-sm font-bold">
                                <LogOut size={16} /> Log out
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur border-t border-slate-100 z-50 pb-safe">
                <div className="flex justify-around items-center h-16 max-w-lg mx-auto">
                    {TABS.map(t => (
                        <NavItem key={t.key} icon={t.icon} label={t.label}
                            isActive={!moreOpen && t.match(location.pathname)}
                            onClick={() => go(t.path)} />
                    ))}
                    <NavItem icon={LayoutGrid} label="More"
                        isActive={moreOpen || !onATab}
                        onClick={() => setMoreOpen(v => !v)} />
                </div>
            </div>
        </>
    );
};

export default MobileNavbar;
